import { useState, useEffect, useCallback } from 'react';
import { 
  CreditCard, Users, Linkedin, FileText, Building2, TrendingUp,
  RefreshCw, ExternalLink, MapPin, Briefcase, Users2
} from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { Header } from '@/components';
import { loadCompanies } from '@/services/contacts';
import { API_CONFIG } from '@/services/api';

// API Endpoints
const USERS_API = API_CONFIG.USERS_API;

interface StatsData {
  creditsUsed: number;
  contactsDiscovered: number;
  linkedinProfilesScraped: number;
  linkedin360Reports: number;
  glassdoor360Reports: number;
  crunchbaseReports: number;
}

interface SuggestedCompany {
  companyId: string;
  companyName: string;
  location?: string;
  industry?: string;
  size?: string;
  linkedin?: string;
  website?: string;
}

export function Dashboard() {
  const { userEmail, isAuthenticated } = useAppStore();
  
  const [stats, setStats] = useState<StatsData>({
    creditsUsed: 0,
    contactsDiscovered: 0,
    linkedinProfilesScraped: 0,
    linkedin360Reports: 0,
    glassdoor360Reports: 0,
    crunchbaseReports: 0
  });
  const [suggestedCompanies, setSuggestedCompanies] = useState<SuggestedCompany[]>([]);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async (signal?: AbortSignal) => {
    if (!userEmail) return;
    
    setLoading(true);
    try {
      // Load user stats from API
      const res = await fetch(`${USERS_API}?action=get_stats&email=${encodeURIComponent(userEmail)}`, { signal });
      const data = await res.json();
      if (signal?.aborted) return;
      
      if (data.success && data.stats) {
        setStats({
          creditsUsed: data.stats.credits_used || 0,
          contactsDiscovered: data.stats.contacts_discovered || 0,
          linkedinProfilesScraped: data.stats.linkedin_profiles_scraped || 0,
          linkedin360Reports: data.stats.linkedin_360_reports || 0,
          glassdoor360Reports: data.stats.glassdoor_360_reports || 0,
          crunchbaseReports: data.stats.crunchbase_reports || 0
        });
      }
      
      // Load suggested companies
      const companies = await loadCompanies(userEmail, { signal });
      if (signal?.aborted) return;
      // Get first 10 companies as suggestions
      const suggestions = companies.slice(0, 10).map(c => ({
        companyId: c.companyId,
        companyName: c.companyName,
        location: c.location || '',
        industry: c.industry || '',
        size: c.companySize || '',
        linkedin: c.linkedin,
        website: c.website
      }));
      setSuggestedCompanies(suggestions);
      
    } catch (e: any) {
      if (signal?.aborted || e?.name === 'AbortError') return;
      console.error('Failed to load stats:', e);
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (isAuthenticated && userEmail) {
      const controller = new AbortController();
      loadStats(controller.signal);
      return () => controller.abort();
    }
  }, [isAuthenticated, userEmail, loadStats]);

  const StatCard = ({ 
    title, 
    value, 
    icon: Icon, 
    gradient 
  }: { 
    title: string; 
    value: number; 
    icon: React.ElementType; 
    gradient: string;
  }) => (
    <div className="bg-card-dark border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-400 text-sm mb-1">{title}</p>
          <p className="text-3xl font-bold text-white">{value.toLocaleString()}</p>
        </div>
        <div className={`p-3 rounded-lg ${gradient}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Please Log In</h1>
          <a href="/login" className="text-red-500 hover:text-red-400">Go to Login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />

      {/* Page Header */}
      <div className="px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Dashboard</h2>
            <p className="text-sm text-gray-400 mt-1">Overview of your account activity</p>
          </div>
          <button
            onClick={() => {
              void loadStats();
            }}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="px-6 py-6 space-y-8">
        {/* Stats Grid - 3x2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard 
            title="Total Credits Used" 
            value={stats.creditsUsed} 
            icon={CreditCard}
            gradient="bg-gradient-to-br from-red-500 to-red-700"
          />
          <StatCard 
            title="Contacts Discovered" 
            value={stats.contactsDiscovered} 
            icon={Users}
            gradient="bg-gradient-to-br from-blue-500 to-blue-700"
          />
          <StatCard 
            title="LinkedIn Profiles Scraped" 
            value={stats.linkedinProfilesScraped} 
            icon={Linkedin}
            gradient="bg-gradient-to-br from-sky-500 to-sky-700"
          />
          <StatCard 
            title="LinkedIn 360 Reports" 
            value={stats.linkedin360Reports} 
            icon={FileText}
            gradient="bg-gradient-to-br from-indigo-500 to-indigo-700"
          />
          <StatCard 
            title="Glassdoor 360 Reports" 
            value={stats.glassdoor360Reports} 
            icon={Building2}
            gradient="bg-gradient-to-br from-green-500 to-green-700"
          />
          <StatCard 
            title="Crunchbase Reports" 
            value={stats.crunchbaseReports} 
            icon={TrendingUp}
            gradient="bg-gradient-to-br from-orange-500 to-orange-700"
          />
        </div>

        {/* Suggested Companies */}
        <div className="bg-card-dark border border-gray-700 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white">Suggested Companies</h3>
            <p className="text-sm text-gray-400">Companies from your research ready for intelligence</p>
          </div>
          
          {loading ? (
            <div className="px-6 py-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-500 mx-auto mb-2" />
              <p className="text-gray-500">Loading companies...</p>
            </div>
          ) : suggestedCompanies.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No companies found. Start researching to see suggestions here!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        Location
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase">
                      <div className="flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        Industry
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase">
                      <div className="flex items-center gap-1">
                        <Users2 className="w-3 h-3" />
                        Size
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Website</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestedCompanies.map((company, idx) => (
                    <tr key={company.companyId || idx} className="border-b border-gray-800 hover:bg-gray-900/50">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-white">{company.companyName}</div>
                      </td>
                      <td className="px-6 py-4 text-gray-300 text-sm">{company.location || '—'}</td>
                      <td className="px-6 py-4 text-gray-300 text-sm">{company.industry || '—'}</td>
                      <td className="px-6 py-4 text-gray-300 text-sm">{company.size || '—'}</td>
                      <td className="px-6 py-4 text-gray-300 text-sm">
                        {company.website ? (
                          <a
                            href={`https://${company.website.replace(/^https?:\/\//, '')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                          >
                            {company.website.replace(/^https?:\/\//, '')}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : '—'}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {company.linkedin && (
                            <a 
                              href={company.linkedin} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300"
                              title="View LinkedIn"
                            >
                              <Linkedin className="w-4 h-4" />
                            </a>
                          )}
                          <a
                            href={`/recruiter-intelligence`}
                            className="text-gray-400 hover:text-white"
                            title="Run Intelligence"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Intent Topics - Coming Soon */}
        <div className="bg-card-dark border border-gray-700 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white">Intent Topics</h3>
            <p className="text-sm text-gray-400">Track buying signals and intent data</p>
          </div>
          <div className="px-6 py-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 mb-4">
              <TrendingUp className="w-8 h-8 text-purple-400" />
            </div>
            <h4 className="text-xl font-semibold text-white mb-2">Coming Soon!</h4>
            <p className="text-gray-400 max-w-md mx-auto">
              Intent topics will help you identify companies actively researching solutions like yours. 
              Stay tuned for this powerful feature.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800 px-6 py-4 mt-8">
        <div className="flex justify-between items-center text-sm text-gray-500">
          <div>© 2026 Profitibull. All rights reserved.</div>
          <div className="flex space-x-4">
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
